import type { ReactNode } from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import { UtilityToolPageShell, type UtilityToolShellSkin } from '@saas-pages/shared/public/app-shell'
import branding from '../../../branding.config'
import { seoFor, type AvailableToolKey } from '@/seo/routes'
import { LocalizedProcessingBadge, useT } from '@/shared/i18n'
import {
  CATEGORY_ACCENT,
  buildCategoryLabel,
  buildTools,
  getTool,
  relatedTools,
} from '@/shared/tools/tools'

interface ToolPageShellProps {
  toolKey: AvailableToolKey
  children: ReactNode
  width?: 'default' | 'wide'
}

const devdeckShellSkin: UtilityToolShellSkin = {
  categoryClassName: 'font-mono',
  scenariosCardClassName:
    'overflow-hidden border-primary/25 bg-card shadow-[0_14px_42px_hsl(var(--primary)_/_0.1)] dark:bg-[#12140f] dark:text-lime-100 dark:shadow-[0_14px_42px_hsl(var(--primary)_/_0.14)]',
  scenariosTitleClassName: 'font-mono text-primary dark:text-cyan-200',
  scenarioChipClassName:
    'bg-muted/60 dark:border-white/10 dark:bg-white/[0.06] dark:text-lime-100/75',
  relatedCardClassName: 'transition-all',
  relatedAvailableClassName:
    'hover:-translate-y-0.5 hover:border-primary/45 hover:shadow-[0_12px_32px_hsl(var(--primary)_/_0.12)]',
  relatedIconClassName: 'border-primary/20 bg-primary/10 font-mono text-primary',
}

export function ToolPageShell({ toolKey, children, width = 'default' }: ToolPageShellProps) {
  const t = useT()
  const tools = buildTools(t)
  const categoryLabel = buildCategoryLabel(t)
  const tool = getTool(toolKey, tools)
  const related = relatedTools(toolKey, tools)
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  return (
    <UtilityToolPageShell
      tool={tool}
      relatedTools={related}
      seo={seoFor(toolKey, t, pathname)}
      domain={branding.domain}
      ogImage={branding.ogImage}
      homeLabel={t.common.textTools}
      scenariosLabel={t.common.scenarios}
      howToLabel={t.common.howTo}
      faqLabel={t.common.faq}
      otherToolsLabel={t.common.otherTools}
      allToolsLabel={t.common.allTools}
      categoryLabel={categoryLabel[tool.category]}
      categoryClassName={CATEGORY_ACCENT[tool.category]}
      processingBadge={<LocalizedProcessingBadge variant={tool.processing} />}
      width={width}
      skin={devdeckShellSkin}
      renderLink={({ href, className, children }) => (
        <Link key={href} to={href} className={className}>
          {children}
        </Link>
      )}
    >
      {children}
    </UtilityToolPageShell>
  )
}
